
/**
 * Standard options object to create a new Modal. All are optional.
 */
interface MacrailModalOptions {

    /**
     * ID to give the containing div.
     */
    id?: string;

    /**
     * Extra CSS classes to add to the dialog element.
     */
    dialogClass?: string;

    /**
     * CSS classes to add to the header element. Defaults to 'modal-header-primary'.
     */
    headerClass?: string;

    /**
     * Title to use.
     */
    title?: string;

    /**
     * Main body content. Can be an HTML string or a JQuery object.
     */
    body?: string | JQuery;

    /**
     * Buttons descriptor
     */
    buttons?: MacrailModalButtonDesc[];

    /**
     * Element that that dialog will be appended to. Defaults to '#content-container'.
     */
    container?: JQuery;

    /**
     * Show the close cross in the top right?
     */
    showClose?: boolean;

    /**
     * Should the modal element be completly destroyed when it is closed.
     */
    destroyOnClose?: boolean;

    /**
     * By default, the entire modal will scroll when it is longer then the page. Set fixedEnds to true and the header
     * and footer will always be visible, and just the modal content will scroll.
     */
    fixedEnds?: boolean;

    /**
     * Callback for when the modal is shown.
     */
    onShow?: () => void;

    /**
     * Callback for when the body content has been loaded via AJAX.
     */
    onBodyLoad?: () => void;

    /**
     * Callback for when the modal is hidden.
     */
    onHide?: () => void;

    /**
     * Callback for when a button has been clicked.
     */
    onButtonClick?: (button: any) => void;

    /**
     * Attach Modal to the container or create on the fly
     */
    attachContainer?: boolean;

    /**
     * Bootstrap Modal Options
     */
    bootstrapOptions?: Bootstrap.ModalOptions;

    /**
     * Allow general string indexing.
     */
    [propName: string]: any;
}

/**
 * Description of a button
 */
interface MacrailModalButtonDesc {

    /**
     * Identifier for the button.
     */
    name: string;

    /**
     * Text to display.
     */
    text: string;

    /**
     * CSS class to add.
     */
    class?: string;

    /**
     * Role for button. You can set it to:
     * 'dismiss': will close the modal.
     */
    role?: string;

    /**
     * The Disabled state
     */
    disabled?: boolean;

    /**
     * Display status of the Button
     */

    hidden?: boolean;
}


namespace MACRAIL {

/**
 * Create and show a modal dialog easily.
 */
export class Modal {

    public static defaultButtons = {
        'close': {
            'name': 'close',
            'text': 'Close',
            'role': 'dismiss',
        },
        'okay': {
            'name': 'okay',
            'text': 'OK',
        },
    };

    public static defaultButtonGroups = {
        'close': [
            Modal.defaultButtons.close,
        ],
        'okay': [
            Modal.defaultButtons.okay,
        ],
    };

    public defaultButtonDesc = {
        'class': 'btn-default',
    };

    protected opts: MacrailModalOptions;
    protected dialogDiv: JQuery;
    protected modalDiv: JQuery;
    protected contentDiv: JQuery;
    protected headerDiv: JQuery;
    protected titleDiv: JQuery;
    protected bodyDiv: JQuery;
    protected footerDiv: JQuery;
    protected closeButton: JQuery;
    protected alertDiv: JQuery;
    protected alertCloseButton: JQuery;

    protected alertVisible: boolean = false;

    /**
     * Create a new Modal.
     *
     * @constructor
     * @param opts Options for creating the form.
     */
    constructor (opts: MacrailModalOptions) {
        this.opts = $.extend({
            'title': '',
            'body': '',
            'dialogClass': '',
            'headerClass': 'modal-header-primary',
            'showClose': true,
            'destroyOnClose': false,
            'fixedEnds': true,
            'container': $('#content-container'),
            'attachContainer': true,
            'buttons': Modal.defaultButtonGroups.okay,
            'bootstrapOptions': {
                'keyboard': true,
                'backdrop': true,
            },
        }, opts || {});

        this.modalDiv = $('<div>');
        if (this.opts.id) {
            if (document.getElementById(this.opts.id) !== null) {
                throw new Error('The given id already exists.');
            }
            this.modalDiv.attr('id', this.opts.id);
        } else {
            this.modalDiv.uniqueId();
            this.opts.id = this.modalDiv.attr('id');
        }
        this.dialogDiv = null;
        this.contentDiv = null;
        this.headerDiv = null;
        this.alertDiv = null;
        this.alertCloseButton = null;
        this.bodyDiv = null;
        this.footerDiv = null;

        this._createModal();
    }

    /**
     * Set an option.
     *
     * @param name Option to set, same as the [[MacrailModalOptions]] interface.
     * @param value Value to set.
     */
    public setOption (name: string, value: any): void {
        this.opts[name] = value;

        switch (name) {
            case 'title':
                this.titleDiv.text(this.opts.title);
                break;
        }
    }

    /**
     * Set the buttons visible in the footer.
     */
    public setButtons (buttons: MacrailModalButtonDesc[]): void {
        this.footerDiv.text('');

        let buttonCount = 0;

        for (let button of buttons) {
            buttonCount++;
            let buttonId = this.opts.id + '-button-' + button.name;
            let extraAttrs = '';

            button = $.extend({}, this.defaultButtonDesc, button);
            if (button.role === 'dismiss') {
                extraAttrs += ' data-dismiss="modal"';
            }
            if (button.disabled) {
                extraAttrs += ' disabled';
            }
            if (button.hidden) {
                extraAttrs += ' style="display:none"';
            }
            this.footerDiv.append(
                `<button type="button" class="btn ${button.class}" id="${buttonId}" ${extraAttrs}>` +
                    `${button.text}` +
                `</button>`,
            );
        }

        this.footerDiv.css('display', buttonCount > 0 ? '' : 'none');
    }

    /**
     * Enable or disable a specified button.
     *
     * @param name    string  Name of button to change.
     * @param enabled boolean Whether enabled or not.
     */
    public setButtonEnabled(name: string, enabled: boolean): void {
        let buttonId = '#' + this.opts.id + '-button-' + name;

        $(buttonId).prop('disabled', !enabled);
    }

    /**
     * Show the dialog.
     *
     * @return void
     */
    public show (): void {
        if (this.dialogDiv === null) {
            this._createModal();
        }

        this.clearAlert();

        this.modalDiv.modal(this.opts.bootstrapOptions).modal('show');

        this.resizeBody();

        if (typeof this.opts.onShow === 'function') {
            this.opts.onShow();
        }
    }

    /**
     * Hide the Modal dialog.
     */
    public hide (): void {
        this.modalDiv.modal('hide');
    }

    /**
     * Replace the body with a loading spinner icon.
     *
     * @return void
     */
    public showLoader (): void {
        this.bodyDiv.html(
            `<div style="font-size: 30pt; padding: 40px; text-align: center;">` +
                `<i class="fa fa-spinner fa-pulse"></i>` +
            `</div>`,
        );
    }

    /**
     * GET a URL and put the contents into the body.
     *
     * @param url URL to fetch.
     * @param data Extra paramaters to send.
     *
     * @return void
     */
    public loadBody (url: string, data: any): void {
        $.post(url, data || {})
            .done($.proxy(this._onBodyLoadSuccess, this))
            .fail($.proxy(this._onBodyLoadError, this));
    }

    /**
     * Set the alert displayed at the top of the modal.
     *
     * @param className The alert class to add. Bootstraps comes with `info`, `success`, `warning`, and `danger`.
     * @param html HTML content to show in the alert.
     *
     * @return void
     */
    public setAlert (className: string, html: string): void {
        this.alertDiv.prop('class', 'modal-alert alert alert-' + className);
        let alertColour = this.alertDiv.css('borderLeftColor');
        /*this.contentDiv.css({
            outline: `2px solid ${alertColour}`
        });*/
        this.alertDiv.css({ 'borderBottomColor': alertColour });
        this.alertCloseButton.css({ 'color': alertColour });
        this.alertDiv.find('span').html(html);
        this.alertDiv.show();
        this.alertVisible = true;
        this.resizeBody();
    }

    /**
     * Remove any visible alert.
     *
     * @return void
     */
    public clearAlert (): void {
        this.alertVisible = false;
        this.alertDiv.hide();
        this.resizeBody();
        // this.alertDiv.css('display', 'none').text('');
    }

    /**
     * Actually create the various HTML elements that make up the Model.
     * @return void
     */
    protected _createModal () {
        let self = this;

        // If the modal has already been created, then just return.
        if (this.dialogDiv !== null) {
            return;
        }

        this.modalDiv.attr({
            'aria-hidden': true,
            'aria-labelledby': this.opts.id,
            'role': 'dialog',
            'tabindex': '-1',
        }).addClass('modal fade');

        this.dialogDiv = $('<div>').addClass('modal-dialog ' + this.opts.dialogClass);
        this.contentDiv = $('<div>').addClass('modal-content');
        this.headerDiv = $('<div>').addClass('modal-header ' + this.opts.headerClass);
        this.alertDiv = $('<div><span></span></div>').addClass('modal-alert alert').css('display', 'none');
        this.bodyDiv = $('<div>').addClass('modal-body');
        this.titleDiv = $('<div>').addClass('modal-title').text(this.opts.title);
        this.footerDiv = $('<div>').addClass('modal-footer').css('display', 'none');

        if (this.opts.showClose) {
            this.closeButton = $('<button>').attr({
                'type':         'button',
                'data-dismiss': 'modal',
                'aria-hidden':  true,
            }).addClass('close').html('<i class="fa fa-times"></i>');
            this.headerDiv.append(this.closeButton);
        }

        if (typeof this.opts.body === 'string') {
            if (this.opts.body.substr(0, 1) === '<') {
                this.opts.body = $(this.opts.body);
            }
        }

        if (this.opts.body instanceof jQuery) {
            (<JQuery> this.opts.body).css({ 'display': '' });
        }
        this.bodyDiv.append(this.opts.body);

        if (this.opts.buttons !== undefined) {
            this.setButtons(this.opts.buttons);
        }

        this.footerDiv.on('click', 'button', function(e) {
            let name = e.currentTarget.id;
            name = name.substr(name.lastIndexOf('-') + 1);
            self._onButtonClicked(name, e);
        });

        this.alertCloseButton = $('<button class="close"><i class="fa fa-times"></i></button>').attr({
            'type':         'button',
            'data-dismiss': 'alert',
            'aria-hidden':  true,
        }).on('click', function() {
            self.clearAlert();
        });
        this.alertDiv.prepend(this.alertCloseButton);

        this.headerDiv.append(this.titleDiv);
        this.contentDiv.append(this.headerDiv);
        this.contentDiv.append(this.alertDiv);
        this.contentDiv.append(this.bodyDiv);
        this.contentDiv.append(this.footerDiv);
        this.dialogDiv.append(this.contentDiv);
        this.modalDiv.append(this.dialogDiv);
        if (this.opts.attachContainer) {
            this.opts.container.append(this.modalDiv);
        }

        if (this.opts.fixedEnds) {
            $(window).on('resize', $.proxy(this.resizeBody, this));
            this.modalDiv.on('shown.bs.modal', $.proxy(this.resizeBody, this));
        }

        this.modalDiv.on('hidden.bs.modal', (e) => {
            if (this.opts.destroyOnClose) {
                this.modalDiv.remove();
            }

            if (typeof this.opts.onHide === 'function') {
                this.opts.onHide();
            }
        });
    }

    /**
     * Fix the header and footer if the fixedEnds option is true.
     *
     * @return void
     */
    protected resizeBody (): void {
        if (!this.opts.fixedEnds) {
            return;
        }

        let maxHeight = Math.floor(
            $(window).height() -
            this.headerDiv.outerHeight() -
            (this.alertVisible ? this.alertDiv.outerHeight() : 0) -
            this.footerDiv.outerHeight() -
            parseInt(this.dialogDiv.css('margin-top'), 10) * 2);

        this.bodyDiv.css({
            'maxHeight': `${maxHeight}px`,
            'overflowY': 'scroll',
        });
    }

    protected _onBodyLoadSuccess (data: any, status: string, request: JQueryXHR): void  {
        switch (typeof data) {
            case 'string':
                this.bodyDiv.html(data);
                break;
            case 'object':
                this.bodyDiv.html(data.html);
                break;
            default:
                this._onBodyLoadError(request, 'error');
                break;
        }

        if (typeof(this._formOnBodyLoad) === 'function') {
           this._formOnBodyLoad();
        }

        if (typeof this.opts.onBodyLoad === 'function') {
            this.opts.onBodyLoad();
        }
    }

    protected _onBodyLoadError (request: JQueryXHR, status: string): void {
        let errorMsg = 'An unexpected error occured while loading. Please refresh the page and try again.';

        if (request.responseJSON && request.responseJSON.message) {
            errorMsg += '<br /><br />' + request.responseJSON.message;
        }
        this.setAlert('danger', errorMsg);
        this.bodyDiv.html('');
    }

    protected _onButtonClicked (button: any, e: Event): void {
        if (this.opts.onButtonClick) {
            this.opts.onButtonClick(button);
        }
    }

    protected _formOnBodyLoad (): void {
        // Do nothing.
    }
}

}
